import { CommunityId, genEntityId } from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { inferRouterOutputs } from "@trpc/server";
import { trpc } from "components/rpc/trpc";
import { DeleteButton } from "components/ui/action-button";
import { AutosavingInput } from "components/ui/autosaving-input";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { InlineBanner } from "components/ui/inline-banner";
import { Input } from "components/ui/input";
import { InputWithPrefix } from "components/ui/input-with-prefix";
import { Radio } from "components/ui/radio";
import { SecondaryButton } from "components/ui/secondary-button";
import { Select } from "components/ui/select";
import { Spinner } from "components/ui/spinner";
import React, { useEffect, useRef, useState } from "react";
import { handleEvent } from "util/user-events";

type PaymentMode = "percentage" | "fee" | "schedule";

interface ScheduleRowDraft {
    key: string;
    basis: "percentage" | "fee";
    percentValue: string;
    feeValue: string;
    dueOffsetDays: string;
}

type ApiRow =
    inferRouterOutputs<AppRouter>["communityPaymentSchedule"]["listForCommunity"][number];

const apiRowToDraft = (row: ApiRow): ScheduleRowDraft => ({
    key: row.id,
    basis: row.basis,
    percentValue:
        row.percentageBasisPoints == null
            ? ""
            : (row.percentageBasisPoints / 100).toString(),
    feeValue: row.amountCents == null ? "" : (row.amountCents / 100).toString(),
    dueOffsetDays: row.dueOffsetDays.toString(),
});

const emptyRow = (): ScheduleRowDraft => ({
    key: genEntityId(),
    basis: "percentage",
    percentValue: "",
    feeValue: "",
    dueOffsetDays: "0",
});

const draftToInput = (draft: ScheduleRowDraft, index: number) => ({
    sequence: index + 1,
    basis: draft.basis,
    percentageBasisPoints:
        draft.basis === "percentage"
            ? Math.round((parseFloat(draft.percentValue) || 0) * 100)
            : null,
    amountCents:
        draft.basis === "fee"
            ? Math.round((parseFloat(draft.feeValue) || 0) * 100)
            : null,
    dueOffsetDays: parseInt(draft.dueOffsetDays, 10) || 0,
});

interface Props {
    communityId: CommunityId;
    paymentType: string;
    paymentPercentage: string;
    paymentFee: string;
    onFieldChange: (fieldName: string, newValue: string) => void;
}

export const ContractPaymentScheduleEditor: React.FC<Props> = ({
    communityId,
    paymentType,
    paymentPercentage,
    paymentFee,
    onFieldChange,
}) => {
    const trpcClient = trpc.useTRPC();
    const queryClient = useQueryClient();

    const listOptions = trpcClient.communityPaymentSchedule.listForCommunity.queryOptions(
        { communityId },
    );
    const scheduleQuery = useQuery(listOptions);
    const setSchedule = useMutation(
        trpcClient.communityPaymentSchedule.setForCommunity.mutationOptions(),
    );

    const [drafts, setDrafts] = useState<ScheduleRowDraft[]>([]);
    const draftsRef = useRef(drafts);
    draftsRef.current = drafts;
    const initialized = useRef(false);
    const lastPersisted = useRef<string | null>(null);

    useEffect(() => {
        if (scheduleQuery.data && !initialized.current) {
            const seeded = scheduleQuery.data.map(apiRowToDraft);
            setDrafts(seeded);
            lastPersisted.current = JSON.stringify(seeded.map(draftToInput));
            initialized.current = true;
        }
    }, [scheduleQuery.data]);

    const hasSchedule = drafts.length > 0;
    const mode: PaymentMode = hasSchedule
        ? "schedule"
        : paymentType === "fee"
          ? "fee"
          : "percentage";

    const persist = async (rows: ScheduleRowDraft[]) => {
        const payload = rows.map(draftToInput);
        const serialized = JSON.stringify(payload);
        if (serialized === lastPersisted.current) {
            return;
        }
        try {
            await setSchedule.mutateAsync({
                communityId,
                rows: payload,
            });
            if (rows.length > 0) {
                handleEvent("contract-payment-schedule-save", {
                    rowCount: rows.length,
                });
            } else {
                handleEvent("contract-payment-schedule-remove");
            }
            lastPersisted.current = serialized;
            queryClient.invalidateQueries({ queryKey: listOptions.queryKey });
        } catch {
            initialized.current = false;
            lastPersisted.current = null;
            queryClient.invalidateQueries({ queryKey: listOptions.queryKey });
        }
    };

    const seedRowFromLegacy = (): ScheduleRowDraft => {
        if (paymentType === "fee" && paymentFee) {
            return { ...emptyRow(), basis: "fee", feeValue: paymentFee };
        }
        if (paymentPercentage) {
            return {
                ...emptyRow(),
                basis: "percentage",
                percentValue: paymentPercentage,
            };
        }
        return emptyRow();
    };

    const selectMode = async (next: PaymentMode) => {
        if (next === "schedule") {
            if (hasSchedule) {
                return;
            }
            const seeded = [seedRowFromLegacy()];
            setDrafts(seeded);
            await persist(seeded);
            return;
        }

        if (hasSchedule) {
            const confirmed = await fancyConfirm(
                "Switch payment schedule?",
                "This removes the multi-payment invoice payment schedule for this community and bills a single payment instead.",
                "Yes, switch",
                "Cancel",
            );
            if (!confirmed) {
                return;
            }
            setDrafts([]);
            await persist([]);
        }
        onFieldChange("paymentType", next);
    };

    const updateRow = (index: number, changes: Partial<ScheduleRowDraft>) => {
        setDrafts(prev =>
            prev.map((row, i) => (i === index ? { ...row, ...changes } : row)),
        );
    };

    const commitRow = (index: number, changes: Partial<ScheduleRowDraft>) => {
        const next = drafts.map((row, i) => (i === index ? { ...row, ...changes } : row));
        setDrafts(next);
        void persist(next);
    };

    const addRow = () => {
        setDrafts(prev => [...prev, emptyRow()]);
    };

    const removeRow = (index: number) => {
        const next = drafts.filter((_, i) => i !== index);
        setDrafts(next);
        void persist(next);
    };

    const groupName = `payment-mode-${communityId}`;

    if (scheduleQuery.isLoading) {
        return <Spinner />;
    }

    return (
        <div className="space-y-3">
            <div>
                <div className="font-semibold">Payment Schedule</div>
                <p className="text-sm text-gray-500">
                    How the agency fee is billed for placements at this community. Only
                    the selected option is used when generating invoices.
                </p>
            </div>

            <div className="space-y-3">
                <div className="space-y-1">
                    <Radio
                        name={groupName}
                        label="By percentage"
                        checked={mode === "percentage"}
                        onChange={() => void selectMode("percentage")}
                    />
                    {mode === "percentage" ? (
                        <div className="w-40 pl-6">
                            <AutosavingInput
                                label=""
                                initial={paymentPercentage}
                                onCommit={newVal =>
                                    onFieldChange("paymentPercentage", newVal)
                                }
                            />
                        </div>
                    ) : null}
                </div>

                <div className="space-y-1">
                    <Radio
                        name={groupName}
                        label="By fee"
                        checked={mode === "fee"}
                        onChange={() => void selectMode("fee")}
                    />
                    {mode === "fee" ? (
                        <div className="w-40 pl-6">
                            <AutosavingInput
                                label=""
                                initial={paymentFee}
                                onCommit={newVal => onFieldChange("paymentFee", newVal)}
                            />
                        </div>
                    ) : null}
                </div>

                <div className="space-y-2">
                    <Radio
                        name={groupName}
                        label="Invoice payment schedule (multiple dated payments)"
                        checked={mode === "schedule"}
                        onChange={() => void selectMode("schedule")}
                    />
                    {mode === "schedule" ? (
                        <div className="space-y-2 pl-6">
                            <p className="text-sm text-gray-500">
                                Each row generates one invoice when invoicing a placement
                                at this community. Amounts and due dates can be adjusted
                                at invoice time.
                            </p>
                            {drafts.map((row, index) => (
                                <div
                                    key={row.key}
                                    className="space-y-2 rounded border border-gray-200 p-3">
                                    <div className="text-sm font-medium text-gray-500">
                                        Payment {index + 1}
                                    </div>
                                    <div className="flex flex-wrap items-end gap-3">
                                        <div className="w-36">
                                            <Select
                                                label="Basis"
                                                textSize="sm"
                                                value={row.basis}
                                                onChange={e =>
                                                    commitRow(index, {
                                                        basis: e.target.value as
                                                            | "percentage"
                                                            | "fee",
                                                    })
                                                }>
                                                <option value="percentage">
                                                    Percentage
                                                </option>
                                                <option value="fee">Flat fee</option>
                                            </Select>
                                        </div>
                                        <div className="w-28">
                                            {row.basis === "percentage" ? (
                                                <Input
                                                    label="Percent"
                                                    textSize="sm"
                                                    type="number"
                                                    min="0"
                                                    value={row.percentValue}
                                                    onChange={e =>
                                                        updateRow(index, {
                                                            percentValue: e.target.value,
                                                        })
                                                    }
                                                    onBlur={() =>
                                                        void persist(draftsRef.current)
                                                    }
                                                />
                                            ) : (
                                                <InputWithPrefix
                                                    label="Fee"
                                                    textSize="sm"
                                                    prefix="$"
                                                    type="number"
                                                    min="0"
                                                    value={row.feeValue}
                                                    onChange={e =>
                                                        updateRow(index, {
                                                            feeValue: e.target.value,
                                                        })
                                                    }
                                                    onBlur={() =>
                                                        void persist(draftsRef.current)
                                                    }
                                                />
                                            )}
                                        </div>
                                        <div className="w-48">
                                            <Input
                                                label="Due (days after move-in)"
                                                textSize="sm"
                                                type="number"
                                                min="0"
                                                value={row.dueOffsetDays}
                                                onChange={e =>
                                                    updateRow(index, {
                                                        dueOffsetDays: e.target.value,
                                                    })
                                                }
                                                onBlur={() =>
                                                    void persist(draftsRef.current)
                                                }
                                            />
                                        </div>
                                        <DeleteButton
                                            backgroundColor="bg-white"
                                            confirm={{
                                                title: "Remove payment?",
                                                message:
                                                    "Remove this payment from the schedule? This takes effect immediately.",
                                                confirmLabel: "Remove",
                                            }}
                                            onClick={() => removeRow(index)}
                                        />
                                    </div>
                                </div>
                            ))}
                            <div className="flex items-center gap-3">
                                <SecondaryButton onClick={addRow}>
                                    Add payment
                                </SecondaryButton>
                                {setSchedule.isPending ? <Spinner /> : null}
                            </div>
                            {setSchedule.isError ? (
                                <InlineBanner type="error">
                                    Saving the payment schedule failed — your latest
                                    change was not saved.
                                </InlineBanner>
                            ) : null}
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};
